import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import { memo } from "react"
import { makeStyles } from "tss-react/mui"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    CreateOrderOnBehalfOfQuery,
    fetchOrderOnBehalfOfCompaniesForServiceCenter,
    OrderOnBehalfOfCompaniesResponse,
} from "../../../../../../shared/components/createOrder/orderOnBehalf/orderOnBehalfFlowApi"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { selectEffectiveServiceCenterId } from "../../../../../context/contextSelectors"
import { AccountingCustomer } from "../../../../../merchant/accounting/accounting/accountingApi"
import { useAppSelector } from "../../../../../store/storeHooks"

// `null` value means "All tenants" (no tenant filter is applied).
const ALL_TENANTS = "ALL"

const selectTenants = (data: OrderOnBehalfOfCompaniesResponse) => data?.customers || []

const useStyles = makeStyles()((theme) => ({
    input: {
        width: "16rem",
        backgroundColor: theme.palette.common.white,
    },
}))

type TenantSelectorProps = Readonly<{
    value: AccountingCustomer | null
    onChange: (tenant: AccountingCustomer | null) => void
}>

export const TenantSelector = memo(({ value, onChange }: TenantSelectorProps) => {
    const { classes } = useStyles()
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)

    const { data: tenants = [] } = useApiQuery(fetchOrderOnBehalfOfCompaniesForServiceCenter, {
        queryName: CreateOrderOnBehalfOfQuery.COMPANIES_SERVICE_CENTER,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
        },
        enabled: Boolean(serviceCenterId),
        select: selectTenants,
    })

    return (
        <FormControl size="small" className={classes.input}>
            <LocalizedStrict id="service-center-reports-statistic-customer-usage-filter-customer-label">
                <InputLabel id="usage-per-tenant-tenant-label">Tenant</InputLabel>
            </LocalizedStrict>
            <LocalizedStrict id="service-center-usage-per-tenant-filter-tenant-label" attrs={{ label: true }}>
                <Select
                    labelId="usage-per-tenant-tenant-label"
                    label="Tenant"
                    value={value ? String(value.id) : ALL_TENANTS}
                    onChange={(event) => {
                        const next = event.target.value
                        onChange(
                            next === ALL_TENANTS
                                ? null
                                : (tenants.find((tenant) => String(tenant.id) === next) ?? null),
                        )
                    }}
                >
                    <MenuItem value={ALL_TENANTS}>
                        <LocalizedStrict id="service-center-usage-per-tenant-tenant-all">
                            <span>All tenants</span>
                        </LocalizedStrict>
                    </MenuItem>
                    {tenants.map((tenant) => (
                        <MenuItem key={tenant.id} value={String(tenant.id)}>
                            {tenant.name}
                        </MenuItem>
                    ))}
                </Select>
            </LocalizedStrict>
        </FormControl>
    )
})
